import React from "react";
import useMediaMetadata from "@src/features/libraryItem/course/hooks/useMediaMetadata";
import { useOpenGraphDataQuery } from "@src/features/contentDetail/ui/MediaPreview.generated";
import useTranslationModeContext from "@src/features/libraryItem/course/hooks/useTranslationModeContext";
import { VideoRefMapContext } from "@src/features/libraryItem/course/contexts/VideoRefMapContext";
import OpenGraphLinkPreview, {
  OgData,
} from "@src/features/libraryItem/course/OpenGraphLinkPreview";
import { LinkType } from "@src/lib/utils/files";
import VideoPlayer from "@src/lib/ui/VideoPlayer";
import {
  getMediaUrl,
  isVimeoRedirectLink,
} from "@src/features/contentDetail/utils/media";
import OpenGraphVideoPlayerIframe from "@src/features/libraryItem/course/OpenGraphVideoPlayerIframe";
import PdfPreview from "@src/features/messaging/PdfPreview";
import ImagePreview from "@src/features/messaging/ImagePreview";
import { SentMessage_MessageBlastMediaFragment } from "./SentMessage.generated";
import { TrainingResourceVersionType } from "@src/types.generated";
import { cn } from "@src/lib/utils/cn";
import RichTextPreview from "./RichTextPreview";
import ContentAvatar from "@src/features/contentLibrary/ContentAvatar";

type Props = {
  id: string;
  messageBlast: SentMessage_MessageBlastMediaFragment;
  className?: string;
  showRichTextDocPreview?: boolean;
};

const defaultStyles = { width: "100%", height: "100%" };

const MessageBlastMediaPreview: React.FC<Props> = (props) => {
  const trainingResource = props.messageBlast.libraryItem?.trainingResource;

  const mediaUrl = React.useMemo(() => {
    if (
      trainingResource?.publishedVersion?.media &&
      trainingResource?.publishedVersion?.type ===
        TrainingResourceVersionType.Media
    ) {
      return getMediaUrl(trainingResource?.publishedVersion?.media, {
        thumb: true,
      });
    } else if (props.messageBlast.media) {
      return getMediaUrl(props.messageBlast.media, { thumb: true });
    }

    return "";
  }, [props.messageBlast, trainingResource]);

  const { linkType, isVideoOrMuxVideo } = useMediaMetadata(mediaUrl || "");
  const openGraphQuery = useOpenGraphDataQuery({
    variables: {
      url: mediaUrl || "",
    },
  });

  const translationModeContext = useTranslationModeContext();
  const selectedLanguage = React.useMemo(() => {
    if (translationModeContext.translationModeEnabled) {
      return translationModeContext.translationLanguage;
    }
    return "en";
  }, [
    translationModeContext.translationLanguage,
    translationModeContext.translationModeEnabled,
  ]);

  const videoRef = React.useRef<HTMLVideoElement>(null);
  const { addRef: addVideoRef } = React.useContext(VideoRefMapContext);
  const ogData = openGraphQuery.data?.OpenGraphData as OgData | undefined;

  React.useEffect(() => {
    if (isVideoOrMuxVideo) {
      addVideoRef(props.id, videoRef);
    }
  }, [addVideoRef, isVideoOrMuxVideo, props.id]);

  if (
    trainingResource?.publishedVersion?.type ===
    TrainingResourceVersionType.RichTextDocument
  ) {
    const richTextContent =
      trainingResource?.publishedVersion?.richTextDocument?.content.en || "";

    if (props.showRichTextDocPreview) {
      return (
        <div
          className={cn(
            "w-full h-full border border-gray-1 bg-gray-1 pt-4 px-4",
            props.className,
          )}
        >
          <div className="flex size-full h-[100px] items-start justify-center overflow-auto">
            <div className="size-full bg-white p-4 shadow-sm">
              <RichTextPreview
                className="text-[10px]"
                text={richTextContent}
                isSelected={false}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={props.className}>
        <ContentAvatar
          size="40px"
          contentType="Resource"
          style={{ borderRadius: 10 }}
          coverImage={{ emojiIcon: "📄" }}
          defaultBackgroundHashKey={trainingResource?.id ?? "rt-doc"}
        />
      </div>
    );
  }

  if (!linkType || !mediaUrl) return null;

  let content: React.ReactNode = null;

  if ([LinkType.IMAGE, LinkType.GIF].includes(linkType)) {
    content = (
      <ImagePreview
        url={mediaUrl}
        className={cn("w-full h-full object-cover", props.className)}
      />
    );
  } else if (isVideoOrMuxVideo || isVimeoRedirectLink(mediaUrl)) {
    content = (
      <VideoPlayer
        _ref={videoRef}
        videoProps={{
          controls: true,
          style: defaultStyles,
        }}
        src={mediaUrl}
        language={selectedLanguage}
      />
    );
  } else if (linkType === LinkType.PDF) {
    content = <PdfPreview mediaUrl={mediaUrl} />;
  } else if (linkType === LinkType.HOSTED_VIDEO) {
    content = (
      <OpenGraphVideoPlayerIframe ogData={ogData} mediaUrl={mediaUrl} />
    );
  } else {
    content = (
      <OpenGraphLinkPreview
        loading={openGraphQuery.loading}
        ogData={ogData}
        url={mediaUrl}
        ogImageProps={defaultStyles}
      />
    );
  }

  return (
    <div
      className={cn(
        "w-full h-full relative overflow-hidden bg-gray-2 border border-gray-1 rounded-lg",
        props.className,
      )}
    >
      {content}
    </div>
  );
};

export default MessageBlastMediaPreview;
